<template>
  <div class="password-card">
    <div class="password-header">
      <div>
        <h2>{{ headerTitle }}</h2>
        <p>{{ t('passwordSettings.subtitle') }}</p>
      </div>
      <v-icon size="28" color="#EA5545">mdi-shield-key</v-icon>
    </div>

    <v-form ref="form" v-model="valid" @submit.prevent="updatePassword">
      <div class="field-grid">
        <v-text-field
          v-model="userPassword.password"
          :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          :type="showPassword ? 'text' : 'password'"
          :label="labelPassword"
          @click:append="showPassword = !showPassword"
          variant="outlined"
          density="comfortable"
          required
        />
        <v-text-field
          v-model="userPassword.password_confirm"
          :append-icon="showPasswordConfirm ? 'mdi-eye' : 'mdi-eye-off'"
          :type="showPasswordConfirm ? 'text' : 'password'"
          :error-messages="passwordError"
          :label="labelPasswordConfirm"
          @click:append="showPasswordConfirm = !showPasswordConfirm"
          @blur="validatePasswordMatch"
          variant="outlined"
          density="comfortable"
          required
        />
      </div>
      <div class="actions">
        <v-btn
          type="submit"
          color="#EA5545"
          variant="flat"
          :disabled="!isPasswordValid"
        >
          {{ submitLabel }}
        </v-btn>
      </div>
    </v-form>
  </div>
</template>

<script setup>
import { ref, computed, watch, inject } from "vue";
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/userStore'
import { useLanguage } from '@/composables/useLanguage'

const baseCalls = inject('baseCalls')
const loaderStore = inject('loaderStore')
const errorStore = inject('errorStore');
const successStore = inject('successStore');
const { t } = useLanguage()

const userStore = useUserStore()
const { userData } = storeToRefs(userStore)

const valid = ref(false);

const userPassword = ref({
  password: '',
  password_confirm: '',
  action: 'updatepassword'
});

const passwordError = ref('');
const showPassword = ref(false);
const showPasswordConfirm = ref(false);
const headerTitle = computed(() => {
  if (Number(userData.value?.user_no_pass_yet ?? 0) === 1) {
    return t('passwordSettings.title.set')
  }
  return t('passwordSettings.title.update')
})
const labelPassword = computed(() => t('passwordSettings.fields.password'))
const labelPasswordConfirm = computed(() => t('passwordSettings.fields.confirm'))
const submitLabel = computed(() => {
  if (Number(userData.value?.user_no_pass_yet ?? 0) === 1) {
    return t('passwordSettings.submit.set')
  }
  return t('passwordSettings.submit.update')
})

function validatePasswordMatch() {
  if (!userPassword.value.password_confirm) {
    passwordError.value = 'Bitte bestätigen Sie Ihr Passwort';
    return false;
  }
  
  if (userPassword.value.password !== userPassword.value.password_confirm) {
    passwordError.value = 'Die Passwörter stimmen nicht überein';
    return false;
  }
  
  passwordError.value = '';
  return true;
}

watch(() => userPassword.value.password, () => {
  if (userPassword.value.password_confirm) {
    validatePasswordMatch();
  }
});

watch(() => userPassword.value.password_confirm, (newValue) => {
  if (newValue && userPassword.value.password === newValue) {
    passwordError.value = '';
  }
});

const isPasswordValid = computed(() => {
  return userPassword.value.password && 
         userPassword.value.password_confirm && 
         userPassword.value.password === userPassword.value.password_confirm;
});

async function updatePassword() {
  if (!validatePasswordMatch()) {
    errorStore.setError('Die Passwörter stimmen nicht überein');
    return;
  }
  
  loaderStore.setLoader(true, "Passwort wird aktualisiert...");
  
  try {
    const response = await baseCalls.userPost(userPassword.value);
    
    if (response.data && response.data.success) {
      successStore.setSuccess(
        true,
        "Passwort geändert",
        "Ihr Passwort wurde erfolgreich aktualisiert"
      );
      
      userPassword.value.password = '';
      userPassword.value.password_confirm = '';
    } else {
      throw new Error(response.data.message || "Fehler beim Aktualisieren des Passworts");
    }
  } catch (err) {
    errorStore.setError(
      err.message || "Beim Ändern des Passworts ist ein Fehler aufgetreten"
    );
    console.error("Fehler beim Passwort-Update:", err);
  } finally {
    loaderStore.setLoader(false);
  }
}
</script>

<style scoped src="@/assets/styles/sfc/components/objects/user/PasswordView.css"></style>
